import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, LayoutDashboard, Layers, MessageSquare, Megaphone,
  BarChart3, History, Settings, LogOut, UserPlus, Shield, CheckSquare, Calendar
} from 'lucide-react';
import logoImg from '@/assets/Sthitih_Logo.png';
import { useAuthStore } from '@/stores/authStore';

interface SuperAdminMobileHeaderProps {
  onInviteClick?: () => void;
  title?: string;
}//redeploy

export const SuperAdminMobileHeader: React.FC<SuperAdminMobileHeaderProps> = ({
  onInviteClick,
  title = 'Super Admin'
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate('/login');
  };

  const navItems = [
    { label: 'Event Console Dashboard', path: '/events', icon: LayoutDashboard },
    { label: 'Task Management Console', path: '/tasks', icon: CheckSquare },
    { label: 'PS Repositories', path: '/events/global-ps', icon: Layers },
    { label: 'Support Tickets', path: '/events/support', icon: MessageSquare },
    { label: 'Announcements', path: '/events/announcements', icon: Megaphone },
    { label: 'Platform Reports', path: '/events/reports', icon: BarChart3 },
    { label: 'Audit Logs', path: '/events/audit-logs', icon: History },
    { label: 'Platform Settings', path: '/settings', icon: Settings },
  ];

  const isActive = (path: string) => {
    if (path === '/settings') {
      return location.pathname === '/settings' || location.pathname === '/events/settings';
    }
    return location.pathname === path;
  };

  return (
    <>
      {/* Top Mobile Bar - Visible ONLY on small screens (< md) */}
      <header className="md:hidden sticky top-0 z-40 bg-[#FFFDF9] border-b border-[#E4D8C8] px-4 py-3 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-xl bg-[#F7F2EA] border border-[#DDD0BE] text-[#243238] hover:bg-[#EFE5D6] transition-colors cursor-pointer"
            aria-label="Open Navigation Menu"
          >
            <Menu className="w-5 h-5 text-[#1F5A43]" />
          </button>

          <div className="flex items-center gap-2.5">
            <img src={logoImg} alt="Sthitih" className="h-7 w-auto object-contain" />
            <div>
              <h1 className="text-xs font-black text-[#243238] leading-tight truncate max-w-[170px]">{title}</h1>
              <p className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider">Svavas Portal</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onInviteClick && (
            <button
              onClick={onInviteClick}
              className="p-2 rounded-xl bg-[#1F5A43]/10 border border-[#1F5A43]/20 text-[#1F5A43] hover:bg-[#1F5A43] hover:text-white transition-colors cursor-pointer"
              title="Invite Client"
            >
              <UserPlus className="w-4 h-4" />
            </button>
          )}

          <div className="w-8 h-8 rounded-lg bg-[#1F5A43] text-white flex items-center justify-center font-bold text-xs shadow-xs">
            SA
          </div>
        </div>
      </header>

      {/* Slide-out Mobile Side Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <div className="fixed inset-0 z-[1200] md:hidden">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-xs"
            />

            {/* Drawer Content */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              className="absolute top-0 left-0 bottom-0 w-[84%] max-w-sm bg-[#FFFDF9] border-r border-[#E4D8C8] flex flex-col justify-between shadow-2xl overflow-y-auto"
            >
              <div>
                {/* Drawer Header */}
                <div className="p-5 border-b border-[#E4D8C8] flex items-center justify-between bg-[#F7F2EA]">
                  <div className="flex items-center gap-3">
                    <img src={logoImg} alt="Sthitih" className="h-8 w-auto object-contain" />
                    <div>
                      <h2 className="text-sm font-extrabold text-[#243238]">Svavas Super Admin</h2>
                      <p className="text-[10px] font-bold text-[#8A4B22] uppercase tracking-wider">Administration Console</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDrawerOpen(false)}
                    className="p-1.5 rounded-xl hover:bg-[#EFE5D6] text-[#6F706C] transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Profile Card */}
                <div className="p-4 mx-4 my-4 rounded-2xl bg-[#F7F2EA] border border-[#DDD0BE] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1F5A43] text-white flex items-center justify-center font-extrabold text-sm shadow-xs">
                    SA
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-[#243238] truncate">Super Administrator</p>
                    <p className="text-[10px] text-[#6F706C] font-semibold truncate mt-0.5">{user?.email || 'superadmin@svavas.com'}</p>
                  </div>
                </div>

                {/* Navigation Links */}
                <div className="px-4 space-y-1 pb-4">
                  <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-widest px-3 block mb-2">Navigation</span>

                  {navItems.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          setDrawerOpen(false);
                          navigate(item.path);
                        }}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all text-left cursor-pointer ${
                          active
                            ? 'bg-[#1F5A43] text-white shadow-xs'
                            : 'text-[#243238] hover:bg-[#F7F2EA] hover:text-[#1F5A43]'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-[#8A4B22]'}`} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}

                  {onInviteClick && (
                    <button
                      onClick={() => {
                        setDrawerOpen(false);
                        onInviteClick();
                      }}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold text-[#1F5A43] bg-[#1F5A43]/10 hover:bg-[#1F5A43]/20 transition-all text-left cursor-pointer mt-2"
                    >
                      <UserPlus className="w-4 h-4 text-[#1F5A43]" />
                      <span>Invite New Client</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Bottom Drawer Actions */}
              <div className="p-4 border-t border-[#E4D8C8] bg-[#F7F2EA] space-y-2">
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate('/dashboard');
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-[#243238] hover:bg-[#EFE5D6] rounded-xl transition-colors cursor-pointer"
                >
                  <Shield className="w-4 h-4 text-[#1F5A43]" />
                  <span>Portal Selection Home</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-[#D64545] hover:bg-rose-100 rounded-xl transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4 text-[#D64545]" />
                  <span>Sign Out of Portal</span>
                </button>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
